import { inject, Injectable } from '@angular/core';
import { Auth, multiFactor,
  TotpMultiFactorGenerator,
  TotpSecret,
  getAuth } from '@angular/fire/auth';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, from, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { StoreService } from '../store/store.service';
import { CookiesService } from '../cookies/cookies.service';
import { NS_Enums } from 'src/app/static/namespace';
// import { jwtDecode, JwtPayload } from "jwt-decode";
import jwt_decode from 'jwt-decode';
import { Token } from 'src/app/models/app.model';
import { onAuthStateChanged } from 'firebase/auth';
import { SentryService } from '../sentry/sentry.service';
import { PosthogService } from '../posthog/posthog.service';
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private store = inject(StoreService);
  private cookieService: CookiesService = inject(CookiesService);
  private sentryService: SentryService = inject(SentryService);
  private posthogService: PosthogService = inject(PosthogService);

  private sharedCookieInterval: any = null;

  firebaseAuth = inject(Auth);
  afAuth = inject(AngularFireAuth);
  isLoggedIn$: Observable<boolean>;
  user$!: Observable<any>;
  userData: any;

  auth: any;

  /* Not using this flag anywhere */
  public globalAuthInitialized: boolean = false;

  constructor(){
    this.auth = getAuth();
    this.afAuth.authState.subscribe((user) => {
      if(!this.isSelectedPublicPath()){
        if (user) {
          this.globalAuthInitialized = true;
          this.userData = user;
          localStorage.setItem('user', JSON.stringify(this.userData));
        } else {
          /*  [ why this saras_global_userId? ] - When user signedup and loggedinto daton, the global login firebase instance somehow shows user as null and it loggs out automatically */
          const saras_global_userId = this.cookieService.getObject<Token>(NS_Enums.saras_global)?.userId;
          if(this.globalAuthInitialized && !saras_global_userId){
            localStorage.setItem('user', 'null');
            JSON.parse(localStorage.getItem('user')!);
            this.logOut();
          }
        }
      }
    })
 }

 async mfaConfig(){
  try{
    const currentUser = await this.afAuth.currentUser;
    console.log('currentUser *****', currentUser);
    if(currentUser){
      const multiFactorSession = await multiFactor(currentUser).getSession()
      const totpSecret = await TotpMultiFactorGenerator.generateSecret(
        multiFactorSession
      );
      return totpSecret;
    }
    return {code: 'current-user-empty'};
  }catch(e){
    console.log(e);
    return Promise.reject(e);
  }
 }

  get isLoggedIn(): boolean {
    const user: any = this.store.getUser();
    return user && user.emailVerified !== false ? true : false;
  }

    // Add this helper function to your service
  private convertObservable<T>(obs: any): Observable<T> {
    return new Observable<T>(observer => {
      const subscription = obs.subscribe({
        next: (value: T) => observer.next(value),
        error: (err: any) => observer.error(err),
        complete: () => observer.complete()
      });
      return () => subscription.unsubscribe();
    });
  }

  isAuthenticated(): Observable<boolean> {
    const authState = this.convertObservable<firebase.default.User | null>(this.afAuth.authState);
    return authState.pipe(map(user => !!user));
  }

  logOut(reroute: boolean = true) {
    this.store.setSarasPortalData('LOGOUT');
    this.store.setUser(null);
    this.store.setToken(null);
    this.cookieService.removeSharedCookie();
    this.afAuth.signOut();
    this.globalAuthInitialized = false;
    this.sentryService.clearUserInfo();
    this.posthogService.reset();
    if(reroute){
      setTimeout(() => {
        this.router.navigate(['login'], {queryParams: {'productType': this.store.getProduct().toLowerCase()}});
      }, 500);
    }
  }

  isTokenExpired(): Observable<boolean> {
    try {
      const token = this.cookieService.getObject<any>(NS_Enums.cacheKeysEnum.saras_auth_key);
      if (!token) return of(true);

      const decodedToken = jwt_decode<any>(token);
      const currentTime = Math.floor(Date.now() / 1000);

      return of(decodedToken.exp < currentTime);
    } catch (error) {
      console.error('Error checking token expiry', error);
      return of(true);
    }
  }

  refreshToken(): Observable<any> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return of(null); // or handle the absence of a refresh token appropriately
    }

    return from(
      this.afAuth.currentUser.then(user => {
        if (user) {
          // Force refresh the token
          const token = user.getIdToken(true);
          return token
        } else {
          throw new Error('No user logged in');
        }
      })
    ).pipe(
      tap(token => {
        this.storeNewAccessToken(token); // Store the token in local storage
      })
    );;
  }

  refreshAuthIfExpired(){
    return this.isTokenExpired().pipe(
      switchMap(expired => {
        if (expired) {
          // Token is expired, get a new one
          return this.refreshToken();
        } else {
          // Token is valid, proceed with the original request
          return of(true);
        }
      }),
      catchError((err)=>{
        return of(false);
      })
    )
  }

  getCurrentUser(){
    this.afAuth.currentUser.then((res)=>{
    })
  }

  pollSharedCookieStatus(){
    try{
      let initial_saras_global = this.cookieService.get(NS_Enums.saras_global) || null;
      this.sharedCookieInterval = setInterval(()=>{
        let saras_global: any = this.cookieService.get(NS_Enums.saras_global) || null;
        if(initial_saras_global != saras_global){
          
          if(!saras_global && this.isLoggedIn){
            this.logOut();
          }else if(saras_global?.token){
            this.isTokenExpired().subscribe((expired)=>{
              if(!expired){
                this.globalAuthInitialized = true;
              }
            })
          }

          initial_saras_global = saras_global;
          // this.removeSharedCookieInterval();
        }else if(!saras_global && this.isLoggedIn){
          this.logOut();
        }
      }, 3000);
    }catch(e){
      console.log(e);
    }
  }

  private getRefreshToken(): string | null {
    return this.cookieService.getObject<any>('saras_auth_key');
  }
  private storeNewAccessToken(token: string) {
    try{
      this.cookieService.putObject('saras_auth_key', token, true);
      const userDetails = this.store.getUser();
      const saras_global: Token = this.cookieService.getObject(NS_Enums.saras_global);
      saras_global.token = token;
      if(saras_global && !saras_global.userId){
        saras_global.userId = userDetails.userId;
      }
      this.cookieService.addSharedCookie(saras_global);
    }catch(e){
      console.log(e);
    }
  }
  private isSelectedPublicPath(){
    const path = location.pathname.replace('/', '');
    return path.startsWith('unenroll-mfa') || path.startsWith('unroll-mfa');
  }
}

